<template>
  <div
    class="px-1 pt-2"
    :style="showModal ? 'min-height: calc(100vh - 700px) !important;' : ''"
  >
    <div class="flex justify-end mb-2">
      <div class="relative">
        <!-- Button to toggle modal for adding a new constant -->
        <button
          v-if="isEditable"
          @click="toggleModal"
          class="self-settings-add-btn flex items-center text-xs"
        >
          <!-- <span class="text-lg mr-1">+</span>  -->
          <i class="fas fa-plus fa-xs mr-1"></i>
          Add Constant Data
        </button>

        <!-- Modal Dialog for Adding New Constant -->
        <div
          v-if="showModal"
          class="absolute right-full mr-2 top-0 bg-white border border-gray-200 rounded-lg shadow-md p-3 pb-2.5 z-10 w-64"
        >
          <!-- Input for the constant name -->
          <input
            v-model="newConstant.key"
            ref="constantKeyInput"
            type="text"
            placeholder="Enter constant name"
            class="w-full mb-3 px-2 py-1 border rounded text-xs custom-focus"
          />
          <div class="flex justify-end space-x-2">
            <!-- Cancel button to close the modal -->
            <button
              v-if="isEditable"
              @click="toggleModal"
              class="ml-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-1 px-2 rounded text-xs self-settings-cancel-btn"
            >
              Cancel
            </button>
            <!-- Add button to add the constant -->
            <button
              v-if="isEditable"
              @click="addConstant"
              class="text-white font-bold py-1 px-2 rounded text-xs self-settings-add-btn"
              :class="
                !newConstant.key
                  ? 'cursor-not-allowed bg-blue-300'
                  : 'bg-blue-400 hover:bg-blue-500'
              "
              :disabled="!newConstant.key"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Render each constant and allow editing -->
    <div v-for="(constant, index) in localConstants" :key="index">
      <div class="rounded-lg py-1 flex items-center gap-2">
        <!-- Input for constant name -->
        <div class="flex-grow flex items-center gap-2">
          <label class="text-gray-700 font-medium text-xs w-16">Name:</label>
          <input
            :disabled="!isEditable"
            v-model="constant.key"
            type="text"
            class="flex-grow px-3 py-1 border border-gray-300 custom-focus rounded-md"
            placeholder="Enter constant name"
            @input="updateConstantData(constant)"
          />
        </div>
        <!-- Input for constant value -->
        <div class="flex-grow flex items-center gap-2">
          <label class="text-gray-700 font-medium w-16 text-xs">Value:</label>
          <input
            :disabled="!isEditable"
            :value="getDisplayValue(constant.default_value)"
            type="text"
            class="flex-grow px-3 py-1 border border-gray-300 custom-focus rounded-md"
            placeholder="Enter default value"
            @input="handleValueInput(constant, $event)"
          />
        </div>
        <!-- Action Buttons -->
        <div class="flex items-center gap-2">
          <!-- Revert Constant Button -->
          <button
            v-if="isEditable && hasPreviousState(constant.key)"
            @click="revertConstant(constant.key)"
            :disabled="isReverting && isReverting(constant.key)"
            class="text-gray-500 hover:text-blue-500"
            :class="{ 'opacity-50 cursor-not-allowed': isReverting && isReverting(constant.key) }"
            aria-label="Revert constant"
            title="Revert to previous value"
          >
            <i class="fas fa-undo fa-sm"></i>
          </button>
          <!-- Button to remove the constant -->
          <button
            v-if="isEditable"
            @click="removeConstant(constant)"
            class="text-gray-500 hover:text-red-500"
            aria-label="Remove constant"
          >
            <i class="fas fa-trash fa-sm"></i>
          </button>
        </div>
      </div>
    </div>
    <div v-if="localConstants.length > 0" class="border-t my-2 flex">
      <div class="flex items-center gap-2 w-1/2">&nbsp;</div>
      <div class="flex-grow flex gap-2 mt-2">
        <span class="text-xs text-gray-500">
          you can use {{`\{\{`}}  query.<strong>key</strong> {{`\}\}`}} to access query parameters
        </span>
      </div>
    </div>
    <!-- Message when no constants are added -->
    <div v-if="localConstants.length === 0" class="opacity-50 pt-1">
      No constants added yet. Click the +Add button to add a new constant.
    </div>
  </div>
</template>

<script>
export default {
  name: "ConstantDataTab",
  props: {
    isEditable: {
      type: Boolean,
      default: true,
    },
    constants: {
      type: Object,
      default: () => {},
    },
    // Previous states for revert functionality
    previousStates: {
      type: Object,
      default: () => ({}),
    },
    isReverting: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      localConstants: [],
      showModal: false,
      newConstant: { key: "", default_value: "", type: "const" },
    };
  },
  methods: {
    /**
     * Toggles the visibility of the modal for adding new constant data.
     * Resets the new constant data when closing the modal.
     */
    toggleModal() {
      this.showModal = !this.showModal;
      if (!this.showModal) {
        this.newConstant = { key: "", default_value: "", type: "const" };
      } else {
        this.$nextTick(() => {
          this.$refs.constantKeyInput.focus();
        });
      }
    },

    /**
     * Adds the new constant to the list when the 'Add' button is clicked.
     * Emits an 'add' event with the constant data to the parent.
     */
    addConstant() {
      if (this.newConstant.key.trim()) {
        let name = this.newConstant.key.trim();
        let type = this.constants.type;
        this.$emit("add", {
          name,
          type,
        });
        this.toggleModal();
      }
    },

    /**
     * Removes a constant from the list.
     * Displays a confirmation dialog before emitting the 'remove:item' event.
     * @param {Object} constant - The constant object to remove.
     */
    removeConstant(constant) {
      if (window.confirm("Are you sure?")) {
        this.$emit("remove:item", constant.idx);
      }
    },

    /**
     * Updates the constant data when a user makes changes in the input fields.
     * Emits an 'update:item' event with the updated constant.
     * @param {Object} constant - The constant object being updated.
     */
    updateConstantData(constant) {
      this.$emit("update:item", constant);
    },

    /**
     * Check if a constant has a previous state available for revert
     * @param {string} key - The key/name of the constant
     * @returns {boolean}
     */
    hasPreviousState(key) {
      return this.previousStates && this.previousStates[key] !== undefined;
    },

    /**
     * Revert a constant to its previous value
     * @param {string} key - The key/name of the constant to revert
     */
    revertConstant(key) {
      this.$emit("revert", key);
    },
    /**
     * Get display value for input (stringify arrays/objects)
     */
    getDisplayValue(value) {
      if (value === null || value === undefined) return '';
      if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
        return JSON.stringify(value);
      }
      return String(value);
    },
    /**
     * Handle value input - try to parse JSON, fallback to string
     */
    handleValueInput(constant, event) {
      const inputValue = event.target.value.trim();
      let parsedValue = inputValue;
      
      // Try to parse as JSON if it looks like JSON
      if (inputValue.startsWith('[') || inputValue.startsWith('{')) {
        try {
          parsedValue = JSON.parse(inputValue);
        } catch (e) {
          // If JSON parse fails, keep as string
          parsedValue = inputValue;
        }
      }
      
      constant.default_value = parsedValue;
      this.updateConstantData(constant);
    },
  },
  watch: {
    /**
     * Watcher to track changes in the 'constants' prop.
     * Updates the localConstants data whenever the prop changes.
     * @param {Object} newConstants - New constant data passed via prop.
     */
    constants: {
      handler(newConstants) {
        this.localConstants = [...newConstants.data];
      },
      immediate: true,
      deep: true,
    },
  },
  created() {
    /**
     * Sets the initial local constants from the constants prop when the component is created.
     */
    this.localConstants =
      this.constants.data.length > 0 ? [...this.constants.data] : [];
  },
};
</script>
